import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	viewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	ColumnChartConfig,
	isPaletteCustomOverridesActive,
} from '../../../models/chart-config.model';
import { ColumnChartData } from '../../../models/chart-data.model';
import { ThemeConfig } from '../../../models/theme.model';
import { DataService } from '../../../services/data.service';
import {
	highchartsPaletteForThemeMode,
	pinHighchartsPaletteAndTooltipForThemeMode,
	pinHighchartsPaletteForThemeMode,
} from '@framework/analytics/utils/highcharts-palette-for-theme.util';
import {
	explicitChartTooltipHtmlTheme,
	resolveChartThemeMode,
} from '@framework/analytics/utils/resolve-chart-theme-mode.util';
import { PaletteService } from '../../../services/palette.service';
import { ThemeMode, ThemeService } from '../../../services/theme.service';
import type {
	ColorType,
	OptionsStackingValue,
	SeriesOptionsType,
} from 'highcharts';
import Highcharts from '../highcharts.config';
import { JeptoColumnChartDefaults as columnChartDefaults } from './jepto-column-chart.defaults';
import { parseChartVisualizationInput } from '../../../utils/parse-chart-visualization-input.util';
import {
	resolveHighchartsCssColors,
	createHighchartsResizeObserver,
	reflowHighchartsToContainer,
	scheduleHighchartsReflowAfterLayout,
} from '../../../utils/resolve-highcharts-css-colors.util';
import { applyLegendLayoutToChartOptions } from '../../../utils/chart-legend-scale.util';
import { ChartRenderCoordinator } from '../../../utils/chart-render-coordinator.util';
import {
	applyCategoryBreakdownStackingToOptions,
	categoryBreakdownSeriesColor,
	resolveCartesianStacking,
	shouldStackCategoryBreakdown,
} from '../../../utils/category-breakdown-visualization.util';
import { createCartesianCategoryChartTooltipFormatter } from '../../../utils/chart-comparison-tooltip.util';
import {
	chartDateCategoryGranularity,
	formatDateBucketLabel,
} from '../../../utils/date-bucket-label.util';

interface ColumnChartDataPoint {
	name: string;
	y: number;
	color?: string;
}

interface ColumnChartSeries {
	name: string;
	data: ColumnChartDataPoint[] | number[];
	color?: string;
	label?: string;
	aggregationValue?: number;
	comparisonAggregationValue?: number;
	comparisonData?: Array<[string, number]> | unknown[];
	custom?: Record<string, unknown>;
}

interface ChartClickEvent {
	point: { x: number | string; y: number; series: string };
	chart: { type: string; id?: string };
}

/**
 * Column Chart Component (vertical bars) with signal-based reactivity
 */
@Component({
	selector: 'jepto-column-chart',
	templateUrl: './jepto-column-chart.component.html',
	styleUrls: ['./jepto-column-chart.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule],
})
export class JeptoColumnChartComponent implements AfterViewInit, OnDestroy {
	private static readonly MIN_LIKELY_TIMESTAMP_MS = 10_000_000_000; // ~1970-04-26
	private readonly shortDateFormatter = new Intl.DateTimeFormat(undefined, {
		month: 'short',
		day: 'numeric',
	});

	// Signal inputs
	cardId = input<string>();
	config = input<ColumnChartConfig>();
	data = input<ColumnChartData>();
	themeMode = input<ThemeMode>('auto');
	palette = input<string>('pal_jepto');
	themeConfig = input<ThemeConfig>();
	height = input<string>('100%');
	width = input<string>('100%');
	showLegend = input<boolean>(true);
	enableTooltip = input<boolean>(true);
	stacked = input<boolean>(false);

	// Outputs
	chartClick = output<ChartClickEvent>();
	seriesClick = output<ChartClickEvent>();

	// View queries
	private chartContainer = viewChild.required<ElementRef>('chartContainer');

	// Services
	private themeService = inject(ThemeService);
	private paletteService = inject(PaletteService);
	private dataService = inject(DataService, { optional: true });

	// Internal state
	private chart?: Highcharts.Chart;
	private resizeObserver?: ResizeObserver;
	private initialized = false;
	private renderCoordinator = new ChartRenderCoordinator();

	// Computed signals
	/** Set when the category is the card's grouped date: keys stay raw, labels are buckets. */
	private dateCategoryGranularity = computed(() =>
		chartDateCategoryGranularity(this.config()?.data)
	);

	private effectiveData = computed(() => {
		const direct = this.data();
		if (direct) return direct;

		const id = this.cardId();
		if (id && this.dataService) {
			const serviceData = this.dataService.cardDataSignal(id)();
			if (serviceData?.chartType === 'column')
				return serviceData as unknown as ColumnChartData;
		}
		return null;
	});

	private paletteColors = computed(() => {
		const cfg = this.config();
		const paletteId = cfg?.palette?.selectedPaletteId ?? this.palette();
		const resolvedMode = resolveChartThemeMode(
			this.themeMode(),
			this.themeService.getActiveThemeModeSignal()()
		);

		const overrides = isPaletteCustomOverridesActive(cfg?.palette);
		const customLight = overrides ? cfg?.palette?.customLight : undefined;
		const customDark = overrides ? cfg?.palette?.customDark : undefined;

		const palette = this.paletteService.getPaletteById(paletteId);
		if (palette) {
			if (resolvedMode === 'dark' && customDark?.length) {
				return customDark;
			}
			if (resolvedMode === 'light' && customLight?.length) {
				return customLight;
			}
			return resolvedMode === 'dark' ? palette.dark : palette.light;
		}

		// Fallback for non-standard palette identifiers.
		if (cfg?.palette?.selectedPaletteId) {
			return this.paletteService.getColorsForPaletteWithOverrides(
				cfg.palette.selectedPaletteId,
				customLight,
				customDark,
				resolvedMode
			);
		}
		return this.paletteService.getColorPalette(paletteId);
	});

	private resolvedVisualization = computed(() => {
		const raw = this.config()?.visualization;
		const parsed = parseChartVisualizationInput(raw) as unknown;

		if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
			return parsed as Highcharts.Options;
		}

		return undefined;
	});

	private chartData = computed(() => {
		const sourceData = this.effectiveData();
		if (!sourceData?.data?.series)
			return {
				series: [] as ColumnChartSeries[],
				categories: [] as string[],
			};

		const categories: string[] = [];
		const series: ColumnChartSeries[] = [];

		if (sourceData.data.series.length > 0) {
			sourceData.data.series.forEach(s => {
				s.data.forEach(point => {
					const categoryKey = this.getCategoryKey(point);
					if (categoryKey && !categories.includes(categoryKey)) {
						categories.push(categoryKey);
					}
				});
			});

			// Aggregate-only payloads can come as [null, value].
			// Keep the bars visible but avoid rendering "null" as a category label.
			if (categories.length === 0) {
				categories.push('');
			}

			sourceData.data.series.forEach(s => {
				const transformedData: number[] = [];

				categories.forEach(category => {
					const point = s.data.find(p => {
						return this.getCategoryKey(p) === category;
					});

					if (point) {
						transformedData.push(this.getPointValue(point));
					} else {
						transformedData.push(0);
					}
				});

				series.push({
					name: s.name,
					label: (s as any).label || s.name,
					data: transformedData,
					color: (s as any).color,
					aggregationValue: (s as any).aggregationValue,
					comparisonAggregationValue: (s as any)
						.comparisonAggregationValue,
					comparisonData: (s as any).comparisonData,
					custom: (s as any).custom || {},
				});
			});
		}

		return { series, categories };
	});

	private chartOptions = computed(() => {
		const baseOptions = { ...columnChartDefaults };
		const cfg = this.config();
		const visualization = this.resolvedVisualization();
		const vizColors = visualization?.colors;
		const paletteColorsArr = this.paletteColors();
		const colors =
			Array.isArray(vizColors) && vizColors.length > 0
				? vizColors
				: paletteColorsArr;
		const { series, categories } = this.chartData();
		const id = this.cardId();
		const formatCategoryLabel = (v: string | number) =>
			this.formatCategoryAxisLabel(String(v));

		this.themeConfig();
		const themeModeInput = this.themeMode();
		const pinnedHtmlTheme = explicitChartTooltipHtmlTheme(themeModeInput);
		const resolvedThemeMode = resolveChartThemeMode(
			themeModeInput,
			this.themeService.getActiveThemeModeSignal()()
		);
		this.themeService.themeIdDisplaySignal();

		const dimensions = cfg?.data?.dimensions;
		const sourceSeries = this.effectiveData()?.data?.series ?? [];
		const categoryBreakdown = shouldStackCategoryBreakdown(
			'column',
			dimensions,
			sourceSeries
		);
		const stacking = resolveCartesianStacking('column', {
			stackedInput: this.stacked(),
			categoryBreakdown,
			visualization,
			baseOptions,
		});

		const componentOptions: Partial<Highcharts.Options> = {
			...highchartsPaletteForThemeMode(resolvedThemeMode),
			colors: colors.length > 0 ? colors : undefined,
			chart: {
				...baseOptions.chart,
				backgroundColor: 'var(--chart-background, transparent)',
			},
			title: {
				text: cfg?.title || '',
				style: { color: 'var(--chart-text-color, inherit)' },
			},
			subtitle: {
				text: cfg?.subtitle || '',
				style: { color: 'var(--chart-text-color, inherit)' },
			},
			xAxis: {
				...baseOptions.xAxis,
				type: 'category',
				categories,
				title: {
					text: '',
					style: { color: 'var(--chart-text-color, inherit)' },
				},
				labels: {
					...(Array.isArray(baseOptions.xAxis)
						? {}
						: baseOptions.xAxis?.labels),
					formatter: function (
						this: Highcharts.AxisLabelsFormatterContextObject
					) {
						return formatCategoryLabel(
							this.value as string | number
						);
					},
				},
			},
			yAxis: {
				...baseOptions.yAxis,
				title: {
					text: '',
					style: { color: 'var(--chart-text-color, inherit)' },
				},
			},
			legend: { ...baseOptions.legend, enabled: this.showLegend() },
			tooltip: {
				...baseOptions.tooltip,
				enabled: this.enableTooltip(),
				formatter: createCartesianCategoryChartTooltipFormatter(
					formatCategoryLabel,
					pinnedHtmlTheme
				),
			},
			plotOptions: {
				...baseOptions.plotOptions,
				column: {
					...baseOptions.plotOptions?.column,
					stacking,
					colorByPoint: categoryBreakdown
						? false
						: baseOptions.plotOptions?.column?.colorByPoint,
					point: {
						events: {
							click: (event: any) => {
								this.chartClick.emit({
									point: {
										x:
											event.point.category ||
											event.point.name,
										y: event.point.y,
										series: event.point.series.name,
									},
									chart: { type: 'column', id },
								});
							},
						},
					},
				},
				series: {
					...baseOptions.plotOptions?.series,
					stacking,
					events: {
						click: (event: any) => {
							this.seriesClick.emit({
								point: {
									x:
										event.point?.category ||
										event.point?.name ||
										0,
									y: event.point?.y || 0,
									series: event.target.name,
								},
								chart: { type: 'column', id },
							});
						},
					},
				},
			},
			series: this.transformDataToSeries(
				categoryBreakdown,
				colors,
				stacking
			),
		};

		let merged = Highcharts.merge(baseOptions, componentOptions);
		// JEP-10216: Custom-tab viz often has `series: []`. Highcharts.merge would
		// wipe runtime series — re-pin after merge (same contract as line/area/bar).
		const seriesData = this.transformDataToSeries(
			categoryBreakdown,
			colors,
			stacking
		);
		merged.series = seriesData;
		if (visualization) {
			merged = Highcharts.merge(merged, visualization);
			const vizSeries = visualization.series;
			if (
				Array.isArray(vizSeries) &&
				vizSeries.length > 0 &&
				seriesData.length > 0
			) {
				merged.series = seriesData.map((s, i) => {
					const viz = vizSeries[i] || {};
					const item = Highcharts.merge(
						s,
						viz
					) as Highcharts.SeriesOptionsType;
					// Runtime API data is the source of truth for plotted points.
					(item as Highcharts.SeriesColumnOptions).data = (
						s as Highcharts.SeriesColumnOptions
					).data;
					if (
						viz &&
						(viz as Highcharts.SeriesOptionsType).type !== undefined
					) {
						(item as Highcharts.SeriesColumnOptions).type = (
							viz as Highcharts.SeriesColumnOptions
						).type;
					}
					return item;
				}) as Highcharts.SeriesOptionsType[];
			} else {
				merged.series = seriesData;
			}
		}
		if (categoryBreakdown) {
			merged = applyCategoryBreakdownStackingToOptions(
				merged,
				'column',
				true
			);
		}
		// Pin Chart-tab / resolved stacking onto column + series after viz merge
		// (Highcharts reads type plotOptions; merge can leave series-only stacking).
		// Duplicated in jepto-bar-chart — extract with bar if a third chart needs this.
		if (stacking) {
			merged = {
				...merged,
				plotOptions: {
					...(merged.plotOptions ?? {}),
					column: {
						...(merged.plotOptions?.column ?? {}),
						stacking,
						...(categoryBreakdown ? { colorByPoint: false } : {}),
					},
					series: {
						...(merged.plotOptions?.series ?? {}),
						stacking,
					},
				},
			};
		}
		if (pinnedHtmlTheme) {
			return pinHighchartsPaletteAndTooltipForThemeMode(
				merged,
				resolvedThemeMode,
				createCartesianCategoryChartTooltipFormatter(
					formatCategoryLabel,
					pinnedHtmlTheme
				)
			);
		}
		return pinHighchartsPaletteForThemeMode(merged, resolvedThemeMode);
	});

	constructor() {
		effect(() => {
			const { series } = this.chartData();
			const options = this.chartOptions();
			if (this.initialized && this.chart && series.length > 0) {
				const chart = this.chart;
				this.renderCoordinator.schedule(() => {
					chart.destroy();
					this.renderChart(options);
				});
			}
		});
	}

	ngAfterViewInit(): void {
		this.initializeChart();
		this.setupResizeObserver();
		this.initialized = true;
	}

	ngOnDestroy(): void {
		this.renderCoordinator.cancel();
		this.resizeObserver?.disconnect();
		this.chart?.destroy();
	}

	private renderChart(options: Highcharts.Options): void {
		const container = this.chartContainer().nativeElement;
		const resolved = resolveHighchartsCssColors(
			applyLegendLayoutToChartOptions(options, container),
			container
		);
		this.chart = Highcharts.chart(container, resolved);
		reflowHighchartsToContainer(this.chart, container);
		scheduleHighchartsReflowAfterLayout(() => this.chart, container);
	}

	private initializeChart(): void {
		const container = this.chartContainer();
		if (container) {
			this.renderChart(this.chartOptions());
		}
	}

	private setupResizeObserver(): void {
		this.resizeObserver?.disconnect();
		this.resizeObserver = createHighchartsResizeObserver(
			() => this.chart,
			this.chartContainer().nativeElement
		);
	}

	private transformDataToSeries(
		categoryBreakdown: boolean,
		paletteColors: readonly ColorType[],
		stacking?: OptionsStackingValue
	): SeriesOptionsType[] {
		const { series } = this.chartData();
		if (!series?.length) return [];

		return series.map(
			(s, index) =>
				({
					type: 'column' as const,
					name: s.label || s.name,
					data: s.data,
					color: categoryBreakdown
						? categoryBreakdownSeriesColor(
								paletteColors,
								index,
								s.color
							)
						: s.color || undefined,
					stacking,
					aggregationValue: s.aggregationValue,
					comparisonAggregationValue: s.comparisonAggregationValue,
					comparisonData: s.comparisonData,
					custom: s.custom,
				}) as any
		);
	}

	/** Stable key for deduping / aligning series points (not for display). */
	private getCategoryKey(point: unknown): string {
		if (Array.isArray(point)) {
			return this.categoryKeyFromRaw(point[0]);
		}
		if (typeof point === 'object' && point !== null) {
			const category = (point as any).name ?? (point as any).x;
			return this.categoryKeyFromRaw(category);
		}
		return this.categoryKeyFromRaw(point);
	}

	private categoryKeyFromRaw(raw: unknown): string {
		if (raw === null || raw === undefined) return '';
		const normalized = String(raw).trim();
		if (normalized.toLowerCase() === 'null') return '';
		// Bucket values (`2026-05`, `1`…`7`) keep their value; never parse them as dates.
		if (this.dateCategoryGranularity() !== undefined) return normalized;

		const asNumber = Number(normalized);
		if (
			Number.isFinite(asNumber) &&
			asNumber >= JeptoColumnChartComponent.MIN_LIKELY_TIMESTAMP_MS
		) {
			return String(asNumber);
		}

		const parsed = Date.parse(normalized);
		if (Number.isFinite(parsed)) {
			return String(parsed);
		}

		return normalized;
	}

	/**
	 * Axis / tooltip label for a category key: the bucket label when the category is
	 * the card's grouped date, else a short date (or passthrough).
	 */
	private formatCategoryAxisLabel(key: string): string {
		if (!key) return '';
		const granularity = this.dateCategoryGranularity();
		if (granularity !== undefined) {
			return formatDateBucketLabel(key, granularity);
		}
		const asNum = Number(key);
		if (
			Number.isFinite(asNum) &&
			asNum >= JeptoColumnChartComponent.MIN_LIKELY_TIMESTAMP_MS
		) {
			return this.shortDateFormatter.format(new Date(asNum));
		}
		const parsed = Date.parse(key);
		if (Number.isFinite(parsed)) {
			return this.shortDateFormatter.format(new Date(parsed));
		}
		return key;
	}

	private getPointValue(point: unknown): number {
		if (Array.isArray(point)) return Number(point[1]) || 0;
		if (typeof point === 'object' && point !== null) {
			return Number((point as any).y ?? (point as any).value) || 0;
		}
		return Number(point) || 0;
	}
}
